import { Construction, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';

/**
 * Wraps a consumer-facing page that is currently paused. Admins (is_admin) see
 * the underlying page as normal — they continue to use the surface for
 * curation/testing. Everyone else gets a polite "Coming soon" placeholder.
 *
 * Why a soft-gate instead of a redirect:
 *   - The Sidebar still shows the nav link (with a SOON pill) so consumers
 *     know the surface exists. Clicking it should land on a self-explanatory
 *     screen, not redirect them away to /dashboard without context.
 *
 * Props:
 *   • title    — what's paused (e.g. "Transactions").
 *   • subtitle — optional one-liner about why / when it returns.
 *   • children — the real page; rendered as-is for admins.
 */
export const PausedGate = ({ title, subtitle, children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.is_admin) return children;

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-6 py-12"
      data-testid={`paused-gate-${(title || '').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 border border-accent/30">
          <Construction className="h-6 w-6 text-accent" />
        </div>
        <div className="inline-flex items-center gap-2 mb-3">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Soon</Badge>
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{title}</span>
        </div>
        <h1 className="font-display text-2xl font-semibold text-foreground mb-2">
          We're polishing this.
        </h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {subtitle ||
            `${title} is temporarily paused while we focus on the Recommendation engine and the Chrome Extension. We'll bring it back once the core experience is rock-solid.`}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          data-testid="paused-gate-back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    </div>
  );
};
